import React, { useLayoutEffect, useRef, useState } from 'react'
import { notification } from 'antd'
import { CopyToClipboard } from 'react-copy-to-clipboard/lib/Component'
import copyIcon from 'images/grayCoppy.svg'
import './index.styl'

const CopyButton = ({ text }) => (
  <CopyToClipboard text={text} onCopy={() => notification.success({ message: 'Copied to clipboard' })}>
    <img src={copyIcon} alt='copy' className='copyIcon' />
  </CopyToClipboard>
)

const formatContactHours = contactHours => {
  if (!contactHours || typeof contactHours === 'string') return contactHours || ''
  const formatTime = time => {
    if (time?.hour === undefined || time?.hour === '') return ''
    const minute = String(time.minute ?? 0).padStart(2, '0')
    return `${time.hour}:${minute} ${(time.ampm || '').toUpperCase()}`
  }
  return [formatTime(contactHours.start), formatTime(contactHours.end)].filter(Boolean).join(' - ')
}

const InfoItem = ({ label, value, copyValue, fieldName }) => (
  <div className={['infoItem', fieldName].filter(Boolean).join(' ')}>
    <span className='label'>{label}</span>
    <span className='value'>
      <span className='valueText'>{value}</span>
      {!!copyValue && <CopyButton text={copyValue} />}
    </span>
  </div>
)

const CustomerServiceDetails = ({ customerService }) => {
  const { presentedBy, email, contactPersons } = customerService || {}
  const rowRef = useRef(null)
  const measureRef = useRef(null)
  const [layout, setLayout] = useState({ wrapHours: false, nameLabelWidth: 0, nameWidth: 0, phoneLabelWidth: 0, phoneWidth: 0 })

  useLayoutEffect(() => {
    if (!contactPersons?.length || !rowRef.current || !measureRef.current) return

    const recalculate = () => {
      const availableWidth = rowRef.current?.offsetWidth
      if (!availableWidth) return // container not laid out yet (e.g. modal still opening)

      // Give the hidden replica the exact same width as the live row container,
      // then let it wrap naturally (same CSS as the real row) - this reuses the
      // browser's own flex-wrap decision instead of re-deriving a width budget.
      measureRef.current.style.width = `${availableWidth}px`

      let wrapped = false
      let maxNameLabelWidth = 0
      let maxNameWidth = 0
      let maxPhoneLabelWidth = 0
      let maxPhoneWidth = 0

      Array.from(measureRef.current.children).forEach(row => {
        const [nameItem, phoneItem, hoursItem] = row.children
        if (hoursItem.offsetTop > nameItem.offsetTop) wrapped = true
        maxNameLabelWidth = Math.max(maxNameLabelWidth, nameItem.querySelector('.label').offsetWidth)
        maxNameWidth = Math.max(maxNameWidth, nameItem.querySelector('.valueText').offsetWidth)
        maxPhoneLabelWidth = Math.max(maxPhoneLabelWidth, phoneItem.querySelector('.label').offsetWidth)
        maxPhoneWidth = Math.max(maxPhoneWidth, phoneItem.querySelector('.value').offsetWidth)
      })

      setLayout({
        wrapHours: wrapped,
        nameLabelWidth: maxNameLabelWidth,
        nameWidth: maxNameWidth + 4,
        phoneLabelWidth: maxPhoneLabelWidth,
        phoneWidth: maxPhoneWidth + 4,
      })
    }

    recalculate()
    const observer = new ResizeObserver(recalculate)
    observer.observe(rowRef.current)
    return () => observer.disconnect()
  }, [contactPersons])

  if (!presentedBy && !email && !contactPersons?.length) return null

  return (
    <div className='CustomerServiceDetails'>
      {(!!presentedBy || !!email) && (
        <div className='customerServiceBlock'>
          <div className='sectionTitle'>Customer Service</div>
          <div className='infoRow'>
            {!!presentedBy && <InfoItem label='Presented By:' value={presentedBy} />}
            {!!email && <InfoItem label='Email:' value={email} copyValue={email} />}
          </div>
        </div>
      )}
      {!!contactPersons?.length && (
        <div className='contactBlock'>
          <div className='sectionTitle'>Contact</div>
          <div className='measureFields' ref={measureRef}>
            {contactPersons.map((person, index) => (
              <div className='contactPersonRow' key={index}>
                <InfoItem label='Contact Name:' value={person?.name} />
                <InfoItem label='Phone #' value={person?.phoneNumber} copyValue={person?.phoneNumber} />
                <InfoItem label='Contact Hours:' value={formatContactHours(person?.contactHours)} />
              </div>
            ))}
          </div>
          <div
            className={['contactPersonsList', layout.wrapHours && 'wrapHours'].filter(Boolean).join(' ')}
            style={{
              '--nameLabelWidth': `${layout.nameLabelWidth}px`,
              '--nameColumnWidth': `${layout.nameWidth}px`,
              '--phoneLabelWidth': `${layout.phoneLabelWidth}px`,
              '--phoneColumnWidth': `${layout.phoneWidth}px`,
            }}
            ref={rowRef}
          >
            {contactPersons.map((person, index) => (
              <div className='contactPersonRow' key={index}>
                <InfoItem fieldName='nameField' label='Contact Name:' value={person?.name} />
                <InfoItem fieldName='phoneField' label='Phone #' value={person?.phoneNumber} copyValue={person?.phoneNumber} />
                <InfoItem fieldName='hoursField' label='Contact Hours:' value={formatContactHours(person?.contactHours)} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerServiceDetails
